import { onReducedMotionChange, prefersReducedMotion } from "../helpers/reducedMotion.js";

// Rotates the accented word in the /developer-program/ headline: "Your Business." →
// "Your Experience." → "Your Software." → "Your Future."
//
// The word that ships in the HTML is the first entry of the list, so a crawler, a blocked
// bundle, and the first paint before this file runs all read a finished headline. This
// module only ever replaces it — it never fills an empty element, which is why the mock's
// `{{ word }}` binding is not reproduced literally.
//
// No aria-live on the target, deliberately: the four words are one claim stated four ways,
// not new information, and a headline announced every 2.2s is hostile. The consequence —
// the h1's accessible name changes as it rotates — is accepted, and is why the word in the
// markup is the one that stands alone best.

const INTERVAL_MS = 2200;

// Has to clear the CSS transition on .dp-hero__word (320ms) or the text swaps while the
// element is still visibly fading. The two are a pair; move one and move the other.
const FADE_MS = 340;

class WordCycle {
    constructor(elem) {
        this.elem = elem;
        this.index = 0;
        this.interval = null;
        this.fadeTimer = null;

        // A malformed data-words is a copy-side mistake, not a runtime one, so it degrades
        // to "the headline does not cycle" rather than throwing out of the module and
        // taking the rest of main.js with it.
        try {
            this.words = JSON.parse(elem.dataset.words);
        } catch {
            this.words = null;
        }

        // Fewer than two words is nothing to cycle between; the shipped word already stands.
        if (!Array.isArray(this.words) || this.words.length < 2) return;

        // Resume the sequence from whatever is in the DOM rather than assuming index 0, so
        // the first swap is never a jump back to a word already on screen.
        const current = this.words.indexOf(this.elem.textContent.trim());

        if (current !== -1) this.index = current;

        // Before the first swap, and regardless of the motion preference — reserving the
        // box is not motion, and a visitor who later turns the preference off should not
        // get a reflow along with the first rotation.
        this.reserveWidth();
        window.addEventListener("resize", this.handleResize);

        if (!prefersReducedMotion()) this.start();

        // Both directions, unlike slider.js. There is no in-flight physics to preserve here
        // and no state that gets worse for being restarted — the cost of resuming is one
        // word change, so the preference behaves as a live toggle.
        onReducedMotionChange(this.handleMotionPreference);
    }

    // Holds the element at the width of its widest word. Paired with `text-align: left`
    // in the CSS, that gives the rotation a stationary left edge: every word starts at the
    // same x and longer ones simply run further right. Nothing on the line ever moves.
    //
    // Without it the headline reads as jumpy — it is centred, so it re-centres whenever
    // the word width changes, and at 1440 the four words span 179-301px, which threw the
    // line 61px sideways on every swap.
    //
    // The left alignment is the whole trick, and it is why the box does not need to be
    // per-word or animated: centring the text inside a fixed box instead splits the slack
    // to both sides and opens a visible hole between "Your" and the word.
    //
    // Measured rather than declared in CSS because a word's width is a property of the
    // rendered font, not of its character count, so `ch` or a hardcoded `em` would be a
    // guess that drifts the moment the copy or the face changes. Sizer elements in the
    // markup would work too, but they would put all four words into the h1's textContent,
    // and this repo deliberately keeps that string readable for crawlers that never run
    // CSS (see the note on the homepage h1 in index.njk).
    reserveWidth() {
        const original = this.elem.textContent;

        // Clear first, or each pass measures the previous reservation instead of the text.
        this.elem.style.minWidth = "";

        let widest = 0;

        for (const word of this.words) {
            this.elem.textContent = word;
            widest = Math.max(widest, this.elem.getBoundingClientRect().width);
        }

        this.elem.textContent = original;
        // Ceil so a sub-pixel measurement cannot leave the widest word a fraction short of
        // its own box and wrap it.
        this.elem.style.minWidth = `${Math.ceil(widest)}px`;
    }

    // Type here is fluid (pxv), so the widest word's pixel width changes with the viewport
    // and a reservation made at load is wrong after a resize. Debounced because a pass
    // forces four layouts and resize fires continuously during a drag.
    handleResize = () => {
        clearTimeout(this.resizeTimer);
        this.resizeTimer = setTimeout(() => this.reserveWidth(), 150);
    };

    handleMotionPreference = () => {
        if (prefersReducedMotion()) {
            this.stop();
        } else if (!this.interval) {
            this.start();
        }
    };

    start() {
        this.interval = setInterval(this.advance, INTERVAL_MS);
    }

    stop() {
        clearInterval(this.interval);
        clearTimeout(this.fadeTimer);
        this.interval = null;
        this.fadeTimer = null;
        // Leave whatever word is showing, but never leave it mid-fade — stopping during
        // the 340ms gap would otherwise park the headline at opacity 0.
        this.elem.classList.remove("is-fading");
    }

    advance = () => {
        this.elem.classList.add("is-fading");

        this.fadeTimer = setTimeout(() => {
            this.index = (this.index + 1) % this.words.length;
            this.elem.textContent = this.words[this.index];
            this.elem.classList.remove("is-fading");
        }, FADE_MS);
    };
}

document.querySelectorAll("[data-word-cycle][data-words]").forEach((elem) => new WordCycle(elem));
